import React from 'react';
import { Plus, Search } from 'lucide-react';
import { MeetingTemplate } from '@/types/meeting';
import { TemplateCard } from '../TemplateCard';
import { meetingTemplates } from '../data/meetingTemplates';
import { motion, AnimatePresence } from 'framer-motion';
import { TemplateForm } from './TemplateForm';

export function TemplatesView() {
  const [search, setSearch] = React.useState('');
  const [sortBy, setSortBy] = React.useState<'name' | 'createdAt' | 'updatedAt'>('name');
  const [currentPage, setCurrentPage] = React.useState(1);
  const [showCreateModal, setShowCreateModal] = React.useState(false);
  const [editingTemplate, setEditingTemplate] = React.useState<MeetingTemplate | null>(null);
  const templatesPerPage = 6;

  const filteredTemplates = React.useMemo(() => {
    const filtered = meetingTemplates.filter(template =>
      template.name.toLowerCase().includes(search.toLowerCase()) ||
      template.purpose.toLowerCase().includes(search.toLowerCase()) ||
      template.description.toLowerCase().includes(search.toLowerCase())
    );
    
    return filtered.sort((a, b) => {
      switch (sortBy) {
        case 'name':
          return a.name.localeCompare(b.name);
        case 'createdAt':
          return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
        case 'updatedAt':
          return new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime();
        default:
          return 0;
      }
    });
  }, [search, sortBy]);

  const currentTemplates = React.useMemo(() => {
    const startIndex = (currentPage - 1) * templatesPerPage;
    return filteredTemplates.slice(startIndex, startIndex + templatesPerPage);
  }, [filteredTemplates, currentPage]);

  const handlePageChange = (newPage: number) => {
    setCurrentPage(newPage);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="flex items-center justify-end mb-8">
        <button
          onClick={() => setShowCreateModal(true)}
          className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
        >
          <Plus className="w-4 h-4 mr-2" />
          Create Template
        </button>
      </div>

      <div className="flex items-center justify-between mb-6">
        <div className="relative flex-grow max-w-md">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search templates..."
            className="pl-10 w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
          />
        </div>

        <div className="flex items-center space-x-4">
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value as typeof sortBy)}
            className="rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
          >
            <option value="name">Sort by Name</option>
            <option value="createdAt">Sort by Creation Date</option>
            <option value="updatedAt">Sort by Last Modified</option>
          </select>
        </div>
      </div>
      
      <div className="text-sm text-gray-500 mb-6">
        {filteredTemplates.length} template{filteredTemplates.length !== 1 ? 's' : ''} available
      </div>

      <AnimatePresence mode="wait">
        {(showCreateModal || editingTemplate) && (
          <TemplateForm
            template={editingTemplate || undefined}
            onSubmit={(template) => {
              console.log('Template submitted:', template);
              setShowCreateModal(false);
              setEditingTemplate(null);
            }}
            onClose={() => {
              setShowCreateModal(false);
              setEditingTemplate(null);
            }}
          />
        )}

        <motion.div
          key={currentPage}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -20 }}
          transition={{ duration: 0.2 }}
          className="grid grid-cols-1 md:grid-cols-2 gap-6"
        >
          {currentTemplates.map((template) => (
            <TemplateCard
              key={template.id}
              template={template}
              onSelect={() => {}}
              onEdit={setEditingTemplate}
            />
          ))}
        </motion.div>
      </AnimatePresence>

      {filteredTemplates.length === 0 ? (
        <div className="text-center py-12 text-gray-500">
          No templates found matching your search
        </div>
      ) : (
        <div className="flex items-center justify-center space-x-2 mt-8">
          <button
            type="button"
            onClick={() => handlePageChange(Math.max(currentPage - 1, 1))}
            disabled={currentPage === 1}
            className="inline-flex items-center px-3 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Previous
          </button>
          {Array.from({ length: Math.ceil(filteredTemplates.length / templatesPerPage) }, (_, i) => (
            <button
              key={i + 1}
              onClick={() => handlePageChange(i + 1)}
              className={`
                w-8 h-8 rounded-full text-sm font-medium
                ${currentPage === i + 1
                  ? 'bg-blue-600 text-white'
                  : 'bg-white text-gray-700 hover:bg-gray-50 border border-gray-300'
                }
              `}
            >
              {i + 1}
            </button>
          ))}
          <button
            type="button"
            onClick={() => handlePageChange(Math.min(
              currentPage + 1,
              Math.ceil(filteredTemplates.length / templatesPerPage)
            ))}
            disabled={currentPage === Math.ceil(filteredTemplates.length / templatesPerPage)}
            className="inline-flex items-center px-3 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
}